/**
 * useTransactionCustomFields Hook
 *
 * Returns transaction custom fields from workspace settings filtered by transaction type
 * (expense, income, transfer). Used in the transaction form to show only relevant fields.
 */

import { useEffect, useState } from "react";
import { db } from "../../../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import type {
  TransactionCustomField,
  TransactionCustomFieldScopeType,
  WorkspaceSettings,
} from "../../../types";
import { fieldAppliesToScope } from "../../../utils/transactionCustomFieldsDerivation";

const WORKSPACE_SETTINGS_DOC_ID = "general";

export interface UseTransactionCustomFieldsParams {
  workspaceId: string;
  /** Current transaction type so we only return fields that apply to it */
  transactionType: TransactionCustomFieldScopeType;
}

export interface UseTransactionCustomFieldsResult {
  fields: TransactionCustomField[];
  loading: boolean;
}

export function useTransactionCustomFields({
  workspaceId,
  transactionType,
}: UseTransactionCustomFieldsParams): UseTransactionCustomFieldsResult {
  const [fields, setFields] = useState<TransactionCustomField[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId) {
      setFields([]);
      setLoading(false);
      return;
    }
    const settingsRef = doc(
      db,
      "workspaces",
      workspaceId,
      "settings",
      WORKSPACE_SETTINGS_DOC_ID
    );
    const unsubscribe = onSnapshot(settingsRef, (snap) => {
      if (!snap.exists()) {
        setFields([]);
        setLoading(false);
        return;
      }
      const data = snap.data() as WorkspaceSettings;
      const list = data.transactionCustomFields ?? [];
      const all = Array.isArray(list) ? list : [];
      const filtered = all.filter(
        (f: TransactionCustomField) =>
          !f.hidden && fieldAppliesToScope(f, transactionType)
      );
      const sorted = [...filtered].sort(
        (a: TransactionCustomField, b: TransactionCustomField) =>
          (a.order ?? 0) - (b.order ?? 0)
      );
      setFields(sorted);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [workspaceId, transactionType]);

  return { fields, loading };
}
